/* ===========================================================================
 * hc-collection-filter-ajax.js
 * ---------------------------------------------------------------------------
 * Hangcha NZ - asynchronous collection filtering.
 *
 * Registers <hc-collection-filters>, a vanilla Web Component that mirrors the
 * behaviour of Dawn's <facet-filters-form> but drives the whole machinery shop
 * surface through a single Section Rendering API call:
 *
 *     GET /collections/<handle>?section_id=<id>&<facet params>
 *
 * The response is one fully rendered copy of `main-collection-product-grid`,
 * so the tabs, facet rail, active pills, count, grid and pagination all stay
 * in sync from one fetch. Nothing is re-templated in JavaScript.
 *
 * Progressive enhancement
 *   Every control is a working link or a named form input first. If this file
 *   fails to load or a fetch throws, clicks fall through to ordinary
 *   navigation and the page still filters correctly.
 *
 * Global surface (namespaced, per the theme's JS standards)
 *   window.HCTheme.collection.refresh(searchParams)
 *   window.HCTheme.utils.debounce(fn, wait)
 *
 * Events dispatched on `document` (all namespaced, all CustomEvent)
 *   hc:collection:updating  { searchParams }
 *   hc:collection:updated   { searchParams, productsCount }
 *   hc:collection:error     { searchParams, error }
 * ======================================================================== */

(function () {
  'use strict';

  /* --- Namespace ------------------------------------------------------- */

  window.HCTheme = window.HCTheme || {};
  window.HCTheme.utils = window.HCTheme.utils || {};

  const SELECTORS = {
    facetForm: '[data-hc-facet-form]',
    results: '[data-hc-results]',
    grid: '[data-hc-grid]',
    sort: '[data-hc-sort]',
    error: '[data-hc-error]',
    productCount: '[data-hc-product-count]',
    removeLink: '.js-hc-facet-remove',
    drawer: '[data-hc-facet-drawer]',
    drawerOpen: '[data-hc-facet-open]',
    drawerClose: '[data-hc-facet-close]',
    viewButton: '[data-hc-view]',
  };

  const VIEW_STORAGE_KEY = 'hc:collection:view';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* --- Utilities -------------------------------------------------------- */

  function debounce(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  window.HCTheme.utils.debounce = window.HCTheme.utils.debounce || debounce;

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  /**
   * Reads localStorage behind a guard: Safari private mode and some embedded
   * webviews throw on access rather than returning null.
   */
  function readStoredView() {
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function writeStoredView(value) {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, value);
    } catch (error) {
      /* Non-fatal: the view simply will not persist across page loads. */
    }
  }

  /* --- Component -------------------------------------------------------- */

  class HCCollectionFilters extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.searchParamsPrev = window.location.search.slice(1);
      this.cache = new Map();
      this.abortController = null;

      this.onFormInput = debounce(this.handleFormInput.bind(this), 500);
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onPopState = this.handlePopState.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('input', this.onFormInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);

      this.applyStoredView();

      window.HCTheme.collection = {
        refresh: (searchParams) => this.render(searchParams, true),
        element: this,
      };
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.abortController) this.abortController.abort();
    }

    /* --- Element lookups (re-queried each time: innerHTML is swapped) --- */

    get results() {
      return this.querySelector(SELECTORS.results);
    }

    get errorRegion() {
      return this.querySelector(SELECTORS.error);
    }

    get drawer() {
      return this.querySelector(SELECTORS.drawer);
    }

    /* --- Event handling -------------------------------------------------- */

    handleFormInput(event) {
      const form = event.target.closest(SELECTORS.facetForm);
      if (!form) return;
      // Checkbox and select changes are handled by `change` so they fire
      // immediately; `input` is debounced for the price range number fields.
      if (event.target.type !== 'number' && event.target.type !== 'text') return;
      this.render(this.serialise(form));
    }

    handleChange(event) {
      const sortSelect = event.target.closest(SELECTORS.sort);
      if (sortSelect) {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', sortSelect.value);
        params.delete('page');
        this.render(params.toString());
        return;
      }

      const form = event.target.closest(SELECTORS.facetForm);
      if (!form) return;
      if (event.target.type === 'number' || event.target.type === 'text') return;
      this.render(this.serialise(form));
    }

    handleSubmit(event) {
      const form = event.target.closest(SELECTORS.facetForm);
      if (!form) return;
      event.preventDefault();
      this.render(this.serialise(form));
    }

    handleClick(event) {
      const removeLink = event.target.closest(SELECTORS.removeLink);
      if (removeLink && removeLink.href) {
        event.preventDefault();
        if (removeLink.classList.contains('disabled')) return;
        this.render(new URL(removeLink.href).searchParams.toString());
        return;
      }

      const viewButton = event.target.closest(SELECTORS.viewButton);
      if (viewButton) {
        this.setView(viewButton.dataset.hcView);
        return;
      }

      if (event.target.closest(SELECTORS.drawerOpen)) {
        this.openDrawer();
        return;
      }

      if (event.target.closest(SELECTORS.drawerClose)) {
        this.closeDrawer();
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      const drawer = this.drawer;
      if (drawer && drawer.hasAttribute('open')) this.closeDrawer();
    }

    handlePopState(event) {
      const searchParams = event.state && event.state.hcSearchParams;
      const next = typeof searchParams === 'string' ? searchParams : window.location.search.slice(1);
      if (next === this.searchParamsPrev) return;
      this.render(next, false);
    }

    /* --- Serialisation ---------------------------------------------------- */

    /**
     * Builds the query string from a facet form. Empty values are dropped so
     * the URL stays readable, and `page` is always reset: a narrower result
     * set must not land the customer on an out-of-range page.
     */
    serialise(form) {
      const params = new URLSearchParams(new FormData(form));
      const cleaned = new URLSearchParams();

      params.forEach((value, key) => {
        if (value === '' || value === null) return;
        cleaned.append(key, value);
      });
      cleaned.delete('page');

      return cleaned.toString();
    }

    /* --- Rendering -------------------------------------------------------- */

    async render(searchParams, updateHistory = true) {
      if (searchParams === this.searchParamsPrev) return;
      this.searchParamsPrev = searchParams;

      const url = `${window.location.pathname}?section_id=${encodeURIComponent(
        this.sectionId
      )}${searchParams ? `&${searchParams}` : ''}`;

      this.setBusy(true);
      this.clearError();
      emit('hc:collection:updating', { searchParams });

      try {
        const html = this.cache.has(url) ? this.cache.get(url) : await this.fetchSection(url);
        this.cache.set(url, html);
        this.swap(html);

        if (updateHistory) {
          const next = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
          window.history.pushState({ hcSearchParams: searchParams }, '', next);
        }

        emit('hc:collection:updated', {
          searchParams,
          productsCount: this.readProductCount(),
        });
      } catch (error) {
        if (error.name === 'AbortError') return;

        // Visible, non-silent fallback: tell the customer, and leave the
        // previous results on screen rather than blanking the grid.
        console.error('[HCTheme] Collection update failed:', error);
        this.showError();
        emit('hc:collection:error', { searchParams, error });
      } finally {
        this.setBusy(false);
      }
    }

    async fetchSection(url) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`Section render returned ${response.status} for ${url}`);
      }

      return response.text();
    }

    /**
     * Replaces the component's children with the freshly rendered section's
     * children. Swapping innerHTML (not outerHTML) keeps this element - and
     * therefore its listeners and cache - alive across updates.
     */
    swap(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('hc-collection-filters');
      if (!source) throw new Error('Rendered section did not contain <hc-collection-filters>');

      const drawerWasOpen = this.drawer ? this.drawer.hasAttribute('open') : false;
      const activeId = document.activeElement ? document.activeElement.id : null;

      this.innerHTML = source.innerHTML;
      this.applyStoredView();

      if (drawerWasOpen) this.openDrawer({ restoreFocus: false });

      // Restore focus to the equivalent control so keyboard users are not
      // dumped back at the top of the document after every facet change.
      if (activeId) {
        const restored = this.querySelector(`#${CSS.escape(activeId)}`);
        if (restored) restored.focus({ preventScroll: true });
      }
    }

    readProductCount() {
      const node = this.querySelector(SELECTORS.productCount);
      if (!node) return null;
      const match = node.textContent.replace(/,/g, '').match(/\d+/);
      return match ? Number(match[0]) : null;
    }

    /* --- UI state --------------------------------------------------------- */

    setBusy(isBusy) {
      const results = this.results;
      if (results) results.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      this.querySelectorAll(SELECTORS.removeLink).forEach((link) => {
        link.classList.toggle('disabled', isBusy);
      });
    }

    showError() {
      const region = this.errorRegion;
      if (region) region.setAttribute('data-visible', 'true');
    }

    clearError() {
      const region = this.errorRegion;
      if (region) region.setAttribute('data-visible', 'false');
    }

    /* --- View switcher ---------------------------------------------------- */

    setView(view) {
      if (view !== 'grid' && view !== 'list') return;
      writeStoredView(view);
      this.applyView(view);
    }

    applyStoredView() {
      this.applyView(readStoredView() || this.dataset.defaultView || 'grid');
    }

    applyView(view) {
      const grid = this.querySelector(SELECTORS.grid);
      if (grid) grid.classList.toggle('hc-product-grid--list', view === 'list');

      this.querySelectorAll(SELECTORS.viewButton).forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.hcView === view ? 'true' : 'false');
      });
    }

    /* --- Facet drawer ----------------------------------------------------- */

    openDrawer({ restoreFocus = true } = {}) {
      const drawer = this.drawer;
      if (!drawer) return;

      if (restoreFocus) this.drawerReturnFocus = document.activeElement;
      drawer.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');

      this.querySelectorAll(SELECTORS.drawerOpen).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'true');
      });

      const first = drawer.querySelector('.hc-facet-drawer__panel').querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;

      drawer.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');

      this.querySelectorAll(SELECTORS.drawerOpen).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (this.drawerReturnFocus && document.contains(this.drawerReturnFocus)) {
        this.drawerReturnFocus.focus();
      }
      this.drawerReturnFocus = null;
    }
  }

  if (!customElements.get('hc-collection-filters')) {
    customElements.define('hc-collection-filters', HCCollectionFilters);
  }
})();

/* Append after the native HC collection controller; native fields remain authoritative. */
(() => {
  'use strict';
  if (window.__hcCapacityBandsV1) return;
  window.__hcCapacityBandsV1 = true;
  const NAME = 'filter.p.m.hangcha.load_capacity';
  const INPUT = `input[type="checkbox"][name="${NAME}"]`;
  const ROOT = 'hc-collection-filters.hc-collection';
  const BANDS = [
    ['upto18', '0-1,800 kg', 0, 1800], ['20', '2,000 kg', 2000, 2000],
    ['25to38', '2,500-3,800 kg', 2500, 3800], ['40to55', '4,000-5,500 kg', 4000, 5500],
    ['60to100', '6,000-10,000 kg', 6000, 10000], ['over100', 'Over 10,000 kg', 10000, Infinity]
  ];
  const controllers = new WeakMap(), inventories = new Map(), memory = new Map();
  const sorted = values => [...values].sort();
  const equal = (a, b) => a.size === b.size && [...a].every(v => b.has(v));
  const copy = s => ({ bands: new Set(s.bands), independent: new Set(s.independent) });
  function parseKg(raw) {
    // Verified official Hangcha A Series 14-16 t model (handle a-series-14-16t-four-wheel).
    // This exact legacy field alone is malformed; never generalise its comma correction.
    if (raw === '1,4000~1,6000kg') return [14000, 16000];
    const number = '(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?';
    const m = raw.trim().match(new RegExp(`^(${number})\\s*(?:(?:kg\\s*)?[~\\u2013\\u2014-]\\s*(${number})\\s*)?kg$`, 'i'));
    if (!m) return null;
    const lo = Number(m[1].replaceAll(',', '')), hi = Number((m[2] || m[1]).replaceAll(',', ''));
    return Number.isFinite(lo) && Number.isFinite(hi) && lo >= 0 && hi >= lo ? [lo, hi] : null;
  }
  function mapping(values) {
    return new Map(BANDS.map(([id, , lo, hi]) => [id, new Set([...values].filter(raw => {
      const range = parseKg(raw);
      return range && (hi === Infinity ? range[1] > lo : range[0] <= hi && range[1] >= lo);
    }))]));
  }
  const union = (s, map) => new Set([...s.independent, ...[...s.bands].flatMap(id => [...(map.get(id) || [])])]);
  function removeRaw(state, raw, map) {
    const retained = union(state, map); retained.delete(raw);
    const bands = new Set([...state.bands].filter(id => !map.get(id).has(raw)));
    const owned = union({ bands, independent: new Set() }, map);
    const independent = new Set(state.independent); independent.delete(raw);
    retained.forEach(value => { if (!owned.has(value)) independent.add(value); });
    return { bands, independent };
  }
  const key = values => `hc-capacity-v1:${location.pathname}:${JSON.stringify(sorted(values))}`;
  function remember(s, map) {
    const data = { bands: [...s.bands], independent: [...s.independent] }, k = key(union(s, map));
    memory.set(k, data);
    try { sessionStorage.setItem(k, JSON.stringify(data)); } catch (_) { /* in-tab memory remains */ }
  }
  function restore(actual, map) {
    let data = memory.get(key(actual));
    try { data ||= JSON.parse(sessionStorage.getItem(key(actual)) || 'null'); } catch (_) { /* ignore invalid storage */ }
    if (data && Array.isArray(data.bands) && Array.isArray(data.independent) &&
        data.bands.every(id => map.has(id) && map.get(id).size) && data.independent.every(v => typeof v === 'string')) {
      const state = { bands: new Set(data.bands), independent: new Set(data.independent) };
      if (equal(union(state, map), actual)) return state;
    }
    return { bands: new Set(), independent: new Set(actual) };
  }
  function inventory(root) {
    const cacheKey = `${location.pathname}:${root.dataset.sectionId}`;
    if (!inventories.has(cacheKey)) inventories.set(cacheKey, (async () => {
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('section_id', root.dataset.sectionId);
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Capacity inventory unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const values = new Set([...doc.querySelectorAll(INPUT)].map(input => input.value));
      if (!values.size) throw new Error('Capacity inventory has no native values');
      return mapping(values);
    })());
    return inventories.get(cacheKey);
  }
  function nativeValues(root) {
    const form = root.querySelector('[data-hc-facet-form]');
    return new Set([...(form?.querySelectorAll(INPUT) || [])].filter(x => x.checked).map(x => x.value));
  }
  function sync(root, state, map) {
    const selected = union(state, map);
    root.querySelectorAll('[data-hc-facet-form]').forEach(form => {
      const fields = [...form.querySelectorAll(INPUT)], present = new Set(fields.map(x => x.value));
      selected.forEach(raw => {
        if (present.has(raw)) return;
        const input = document.createElement('input');
        Object.assign(input, { type: 'checkbox', name: NAME, value: raw, hidden: true });
        input.dataset.hcCapacityExtra = 'true'; form.append(input); fields.push(input);
      });
      fields.forEach(input => {
        if (input.disabled) input.dataset.hcCapacityWasDisabled = 'true';
        input.checked = selected.has(input.value);
        if (input.checked) input.disabled = false;
        else if (input.dataset.hcCapacityWasDisabled) input.disabled = true;
        if (!input.checked && input.dataset.hcCapacityExtra) input.remove();
      });
    });
  }
  function hrefFor(values) {
    const url = new URL(location.href);
    url.searchParams.delete(NAME); url.searchParams.delete('page');
    sorted(values).forEach(raw => url.searchParams.append(NAME, raw));
    return `${url.pathname}${url.search}${url.hash}`;
  }
  function rawPill(anchor) {
    const group = anchor.querySelector('.hc-active-filters__pill-group');
    if (!group || !/load\s*capacity|capacity range/i.test(group.textContent)) return null;
    return group.nextElementSibling?.textContent.trim() || null;
  }
  function paint(root, c) {
    const { state, map } = c, owned = union({ bands: state.bands, independent: new Set() }, map);
    root.querySelectorAll('[data-hc-capacity-band]').forEach(input => {
      const values = map.get(input.dataset.hcCapacityBand);
      input.checked = state.bands.has(input.dataset.hcCapacityBand);
      input.indeterminate = false;
      input.disabled = !values.size;
      input.setAttribute('aria-description', 'Matches published family ranges overlapping this band.');
    });
    root.querySelectorAll('[data-hc-capacity-original-note]').forEach(note => {
      note.textContent = state.independent.size ? `Individual selections: ${sorted(state.independent).join('; ')}` : 'Original published values remain available below.';
    });
    root.querySelectorAll('[data-hc-capacity-originals]').forEach(details => {
      details.querySelectorAll(INPUT).forEach(input => {
        const label = input.closest('label');
        if (label) label.title = owned.has(input.value) ? 'Included by a selected capacity range' : '';
      });
    });
    root.querySelectorAll('[data-hc-active-filters]').forEach(list => {
      list.querySelectorAll('[data-hc-capacity-chip]').forEach(li => li.remove());
      list.querySelectorAll('a.hc-active-filters__pill').forEach(anchor => {
        const raw = rawPill(anchor);
        if (raw !== null) {
          const li = anchor.closest('li'); li.hidden = owned.has(raw) && !state.independent.has(raw);
          li.toggleAttribute('data-hc-capacity-owned-hidden', li.hidden);
        }
      });
      state.bands.forEach(id => {
        const next = copy(state); next.bands.delete(id);
        const li = document.createElement('li'), anchor = document.createElement('a');
        li.className = 'hc-active-filters__item'; li.dataset.hcCapacityChip = id;
        anchor.className = 'hc-active-filters__pill js-hc-facet-remove'; anchor.dataset.hcCapacityRemove = id;
        anchor.href = hrefFor(union(next, map));
        const group = document.createElement('span'), value = document.createElement('span'), remove = document.createElement('span');
        group.className = 'hc-active-filters__pill-group'; group.textContent = 'Capacity range';
        value.textContent = BANDS.find(b => b[0] === id)[1]; remove.textContent = ' \u00d7'; remove.setAttribute('aria-hidden', 'true');
        anchor.setAttribute('aria-label', `Remove capacity range ${value.textContent}`);
        anchor.append(group, value, remove); li.append(anchor);
        const clear = list.querySelector('.hc-active-filters__clear')?.closest('li');
        list.insertBefore(li, clear?.parentElement === list ? clear : null);
      });
      const count = [...list.querySelectorAll('a.hc-active-filters__pill')].filter(a => !a.closest('li').hidden).length;
      const label = list.querySelector('.hc-active-filters__label') || list.parentElement.querySelector('.hc-active-filters__label');
      if (label) {
        label.dataset.hcCapacityLabel ||= label.textContent.trim();
        label.textContent = /^\d+\s+selected$/i.test(label.dataset.hcCapacityLabel) ? `${count} selected` :
          `${label.dataset.hcCapacityLabel.replace(/\s*\(\d+\)\s*$/, '')} (${count})`;
      }
    });
    root.querySelectorAll('[data-hc-capacity-details] .hc-facet-filter__selected-count').forEach(el => {
      const count = state.bands.size + state.independent.size; el.textContent = count ? String(count) : ''; el.hidden = !count;
    });
  }
  async function enhance(root) {
    const c = controllers.get(root) || { ticket: 0 };
    controllers.set(root, c); const ticket = ++c.ticket;
    let map;
    try { map = await inventory(root); } catch (_) { return; } // Native controls are the complete fallback.
    if (!root.isConnected || c.ticket !== ticket) return;
    c.map = map; c.state = restore(nativeValues(root), map);
    root.querySelectorAll('[data-hc-facet-form]').forEach((form, formIndex) => {
      const details = form.querySelector(INPUT)?.closest('details');
      if (!details || details.dataset.hcCapacityDetails) return;
      const list = details.querySelector('ul.hc-facet-filter__list'), label = details.querySelector('.hc-facet-filter__summary-label');
      if (!list || !label) return;
      details.dataset.hcCapacityDetails = 'true';
      [...label.childNodes].filter(n => n.nodeType === Node.TEXT_NODE).forEach(n => { n.textContent = 'Capacity range'; });
      const bands = document.createElement('ul'); bands.className = 'hc-facet-filter__list hc-capacity-bands'; bands.setAttribute('role', 'list');
      BANDS.forEach(([id, title]) => {
        const li = document.createElement('li'), item = document.createElement('label'), input = document.createElement('input');
        li.className = 'hc-facet-filter__item'; item.className = 'hc-facet-filter__label';
        input.type = 'checkbox'; input.className = 'hc-facet-filter__input'; input.dataset.hcCapacityBand = id;
        input.id = `HCCapacityBand-${root.dataset.sectionId}-${formIndex}-${id}`;
        const marker = list.querySelector('.hc-facet-filter__marker')?.cloneNode(true) || document.createElement('span');
        marker.classList.add('hc-facet-filter__marker'); marker.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span'); text.className = 'hc-facet-filter__value'; text.textContent = title;
        item.append(input, marker, text); li.append(item); bands.append(li);
      });
      const note = document.createElement('p'); note.className = 'hc-capacity-note'; note.textContent = 'Published family ranges; confirm the model and capacity type. Tow tractor figures are towing ratings.';
      const originals = document.createElement('details'), summary = document.createElement('summary'), selection = document.createElement('p');
      originals.dataset.hcCapacityOriginals = 'true'; summary.textContent = 'Original capacity values'; selection.dataset.hcCapacityOriginalNote = 'true';
      originals.append(summary, selection); list.before(bands, note, originals); originals.append(list);
    });
    sync(root, c.state, map); paint(root, c);
    if (c.focus) {
      const form = [...root.querySelectorAll('[data-hc-facet-form]')].find(f => f.id === c.focus.form);
      const input = form?.querySelector(`[data-hc-capacity-band="${c.focus.band}"]`);
      if (input?.getClientRects().length) input.focus({ preventScroll: true });
      c.focus = null;
    }
  }
  document.addEventListener('change', event => {
    const input = event.target, root = input.closest?.(ROOT), c = root && controllers.get(root);
    if (!c?.state || !input.closest('[data-hc-facet-form]')) return;
    const band = input.dataset.hcCapacityBand;
    if (!band && !input.matches(INPUT)) return;
    const before = union(c.state, c.map);
    if (band) {
      input.checked ? c.state.bands.add(band) : c.state.bands.delete(band);
      c.focus = { form: input.closest('form').id, band };
    } else if (input.checked) c.state.independent.add(input.value);
    else c.state = removeRaw(c.state, input.value, c.map);
    sync(root, c.state, c.map); remember(c.state, c.map); paint(root, c);
    if (equal(before, union(c.state, c.map))) { event.stopPropagation(); c.focus = null; }
  }, true);
  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a.js-hc-facet-remove'), root = anchor?.closest(ROOT), c = root && controllers.get(root);
    if (!c?.state) return;
    const before = union(c.state, c.map), band = anchor.dataset.hcCapacityRemove, raw = rawPill(anchor);
    if (band) c.state.bands.delete(band);
    else if (raw !== null) c.state.independent.delete(raw);
    else if (anchor.classList.contains('hc-active-filters__clear')) c.state = { bands: new Set(), independent: new Set() };
    else return;
    const after = union(c.state, c.map); remember(c.state, c.map);
    if (band || raw !== null) anchor.href = hrefFor(after);
    if (equal(before, after) && (band || raw !== null)) {
      event.preventDefault(); event.stopPropagation(); sync(root, c.state, c.map); paint(root, c);
    }
  }, true);
  function scan() {
    if (!/\/collections\/[^/]+\/?$/.test(location.pathname)) return;
    document.querySelectorAll(ROOT).forEach(root => { if (root.dataset.collectionHandle) enhance(root); });
    if (!document.getElementById('hc-capacity-band-styles')) {
      const style = document.createElement('style'); style.id = 'hc-capacity-band-styles';
      style.textContent = `
        ${ROOT}[data-collection-handle] [data-hc-capacity-owned-hidden] { display:none!important; }
        ${ROOT}[data-collection-handle] .hc-capacity-bands .hc-facet-filter__label,
        ${ROOT}[data-collection-handle] .hc-capacity-note,
        ${ROOT}[data-collection-handle] [data-hc-capacity-originals] > summary,
        ${ROOT}[data-collection-handle] [data-hc-capacity-original-note] { font-family:Arial,sans-serif; text-transform:none; letter-spacing:normal; }
        ${ROOT}[data-collection-handle] .hc-capacity-bands .hc-facet-filter__label,
        ${ROOT}[data-collection-handle] [data-hc-capacity-originals] > summary { color:#1B2E61; }
        ${ROOT}[data-collection-handle] .hc-capacity-note,
        ${ROOT}[data-collection-handle] [data-hc-capacity-original-note] { color:#46536b; font-size:13px; line-height:1.5; }
        ${ROOT}[data-collection-handle] [data-hc-capacity-originals] > summary { cursor:pointer; padding:12px 0; }
        ${ROOT}[data-collection-handle] .hc-capacity-bands input:checked + .hc-facet-filter__marker { background:#1B2E61; border-color:#1B2E61; color:#fff; }
        ${ROOT}[data-collection-handle] .hc-capacity-bands input:focus-visible + .hc-facet-filter__marker,
        ${ROOT}[data-collection-handle] [data-hc-capacity-originals] > summary:focus-visible { outline:3px solid #1B2E61; outline-offset:3px; }
      `;
      document.head.append(style);
    }
  }
  document.addEventListener('hc:collection:updated', scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true }); else scan();
})();

